import { html, css, LitElement, nothing } from "lit";
import { formatAttribute, formatValue, normalizeStatus, normalizeBetterWhen, listRankings, rank, usedMeasures, parse, UnsupportedVersionError, NotOpenResultError } from "@openresult/core";
import { property, state, customElement } from "lit/decorators.js";
const registry = [];
function registerView(plugin) {
  const existing = registry.findIndex((entry) => entry.id === plugin.id);
  if (existing >= 0) {
    registry[existing] = plugin;
    return;
  }
  registry.push(plugin);
}
function registeredViews() {
  return registry;
}
function applicableViews(model) {
  return registry.map((plugin) => ({ plugin, score: safeScore(plugin, model) })).filter((candidate) => candidate.score > 0).sort((a, b) => b.score - a.score || registry.indexOf(a.plugin) - registry.indexOf(b.plugin));
}
function selectView(model, requested) {
  const candidates = applicableViews(model);
  if (candidates.length === 0) return void 0;
  const byId = (id) => id === void 0 ? void 0 : candidates.find((candidate) => candidate.plugin.id === id);
  return byId(requested)?.plugin ?? byId(model.document.presentation?.defaultView)?.plugin ?? candidates[0]?.plugin;
}
function safeScore(plugin, model) {
  try {
    const score = plugin.supports(model);
    return Number.isFinite(score) && score > 0 ? Math.min(score, 1) : 0;
  } catch {
    return 0;
  }
}
const STATUS_LABELS = {
  finished: "finished",
  bye: "bye",
  notClassified: "not classified",
  inProgress: "in progress",
  dnf: "did not finish",
  dns: "did not start",
  dsq: "disqualified",
  outOfTime: "outside the time limit",
  withdrawn: "withdrawn"
};
function statusLabel(status) {
  const normalized = normalizeStatus(status);
  return STATUS_LABELS[normalized] ?? normalized;
}
function isRanked(entry) {
  return entry.rank !== null;
}
function cellText(model, entry, measure) {
  const value = entry.values[measure.id];
  if (value === void 0) {
    return isRanked(entry) ? "—" : statusLabel(entry.result.status);
  }
  return formatValue(value, measure, { locale: model.locale });
}
function attributeText(entry, attribute, locale) {
  const value = entry.result.attributes?.[attribute.id] ?? entry.participant.attributes?.[attribute.id];
  if (value === void 0) return "";
  return formatAttribute(value, attribute, locale === void 0 ? {} : { locale });
}
function visibleAttributes(model) {
  const order = model.document.presentation?.attributeOrder;
  const used = model.attributes.filter(
    (attribute) => model.ranked.some((entry) => attributeText(entry, attribute) !== "")
  );
  if (order === void 0) return used;
  const position = new Map(order.map((id, index) => [id, index]));
  return [...used].sort(
    (a, b) => (position.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (position.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
}
function isHighlighted(model, participantId) {
  return model.document.presentation?.highlight?.includes(participantId) ?? false;
}
function rankingMeasures(model) {
  const active = model.rankings.find((entry) => entry.id === model.activeRanking);
  if (active === void 0) return [];
  return active.sortBy.map((id) => model.measures.find((measure) => measure.id === id)).filter((measure) => measure !== void 0);
}
const tableView = {
  id: "table",
  label: "Table",
  supports: () => 0.2,
  render({ model }) {
    const attributes = visibleAttributes(model);
    const ranked = model.ranked.some((entry) => entry.rank !== null);
    return html`
      <div class="scroll">
        <table>
          <thead>
            <tr>
              ${ranked ? html`<th class="numeric">#</th>` : null}
              <th>Participant</th>
              ${attributes.map((attribute) => html`<th>${attribute.label}</th>`)}
              ${model.measures.map(
      (measure) => html`
                  <th class="numeric">
                    ${measure.label}${measure.unit === void 0 ? null : html`<span class="unit">${measure.unit}</span>`}
                  </th>
                `
    )}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${model.ranked.map(
      (entry) => html`
                <tr
                  class=${isHighlighted(model, entry.participant.id) ? "highlighted" : ""}
                  data-unranked=${entry.rank === null}
                >
                  ${ranked ? html`<td class="numeric rank">${entry.rank ?? "—"}</td>` : null}
                  <td class="name">${entry.participant.name}</td>
                  ${attributes.map(
        (attribute) => html`<td>${attributeText(entry, attribute, model.locale)}</td>`
      )}
                  ${model.measures.map(
        (measure) => html`<td class="numeric">${cellText(model, entry, measure)}</td>`
      )}
                  <td class="status">${statusLabel(entry.result.status)}</td>
                </tr>
              `
    )}
          </tbody>
        </table>
      </div>
    `;
  }
};
const rankingView = {
  id: "ranking",
  label: "Ranking",
  supports: (model) => {
    if (model.rankings.length === 0) return 0;
    return model.ranked.some((entry) => entry.rank !== null) ? 0.9 : 0.3;
  },
  render({ model, onSelect }) {
    const measures = rankingMeasures(model);
    const attributes = visibleAttributes(model).slice(0, 2);
    return html`
      <ol class="standings">
        ${model.ranked.map(
      (entry) => html`
            <li
              class="standing ${isHighlighted(model, entry.participant.id) ? "highlighted" : ""}"
              data-unranked=${entry.rank === null}
              @click=${() => onSelect(entry.participant.id)}
            >
              <span class="position">${entry.rank ?? "—"}</span>

              <span class="who">
                <span class="name">${entry.participant.name}</span>
                ${attributes.length === 0 ? null : html`
                        <span class="attributes">
                          ${attributes.map((attribute) => attributeText(entry, attribute, model.locale)).filter((text) => text !== "").join(" · ")}
                        </span>
                      `}
              </span>

              <span class="figures">
                ${entry.rank === null ? html`<span class="status">${statusLabel(entry.result.status)}</span>` : measures.map(
        (measure) => html`
                          <span class="figure">
                            <span class="value">${cellText(model, entry, measure)}</span>
                            <span class="measure-label">${measure.label}</span>
                          </span>
                        `
      )}
              </span>

              ${entry.tiedWith.length === 0 ? null : html`<span class="tied" title="Level on every sorting measure">tied</span>`}
            </li>
          `
    )}
      </ol>
    `;
  }
};
function withAssets(model) {
  return model.ranked.filter(
    (entry) => (entry.participant.assets?.length ?? 0) > 0 || (entry.result.assets?.length ?? 0) > 0
  ).length;
}
const cardsView = {
  id: "cards",
  label: "Cards",
  supports: (model) => {
    if (model.ranked.length === 0) return 0;
    const illustrated = withAssets(model) / model.ranked.length;
    if (illustrated > 0.5) return 0.95;
    if (model.ranked.length <= 30) return 0.5;
    return 0.1;
  },
  render({ model, onSelect }) {
    const attributes = visibleAttributes(model);
    return html`
      <div class="cards">
        ${model.ranked.map((entry) => {
      const image = entry.participant.assets?.find((asset) => asset.type === "image") ?? entry.result.assets?.find((asset) => asset.type === "image");
      return html`
            <article
              class="card ${isHighlighted(model, entry.participant.id) ? "highlighted" : ""}"
              data-unranked=${entry.rank === null}
              @click=${() => onSelect(entry.participant.id)}
            >
              ${image === void 0 ? null : html`<img
                      class="card-image"
                      src=${image.href}
                      alt=${image.label ?? entry.participant.name}
                      loading="lazy"
                    />`}

              <header class="card-head">
                <span class="position">${entry.rank ?? "—"}</span>
                <h3 class="name">${entry.participant.name}</h3>
              </header>

              ${attributes.length === 0 ? null : html`
                      <dl class="card-attributes">
                        ${attributes.map((attribute) => {
        const text = attributeText(entry, attribute, model.locale);
        return text === "" ? null : html`<dt>${attribute.label}</dt>
                                <dd>${text}</dd>`;
      })}
                      </dl>
                    `}

              <dl class="card-measures">
                ${model.measures.map(
        (measure) => html`
                    <dt>${measure.label}</dt>
                    <dd>${cellText(model, entry, measure)}</dd>
                  `
      )}
              </dl>

              ${entry.rank === null ? html`<p class="status">${statusLabel(entry.result.status)}</p>` : null}
              ${entry.result.notes === void 0 ? null : html`<p class="notes">${entry.result.notes}</p>`}
            </article>
          `;
    })}
      </div>
    `;
  }
};
const compareView = {
  id: "compare",
  label: "Compare",
  supports: (model) => {
    if (model.measures.filter((measure) => ranks(measure)).length < 2) return 0;
    return model.ranked.length >= 2 ? 0.6 : 0;
  },
  render({ model, selection, onSelect }) {
    const chosen = selection.length >= 2 ? selection : defaultSelection(model);
    const entries = chosen.map((id) => model.ranked.find((entry) => entry.participant.id === id)).filter((entry) => entry !== void 0);
    if (entries.length < 2) {
      return html`<p class="empty">Select at least two participants to compare.</p>`;
    }
    return html`
      <div class="compare">
        <p class="compare-hint">
          Comparing ${entries.length} of ${model.ranked.length}. Select a participant in another
          view to change this.
        </p>

        <div class="scroll">
          <table>
            <thead>
              <tr>
                <th>Measure</th>
                ${entries.map(
      (entry) => html`
                    <th class="numeric" @click=${() => onSelect(entry.participant.id)}>
                      ${entry.participant.name}
                      ${entry.rank === null ? null : html`<span class="unit">#${entry.rank}</span>`}
                    </th>
                  `
    )}
              </tr>
            </thead>
            <tbody>
              ${model.measures.map((measure) => {
      const values = entries.map((entry) => entry.values[measure.id]);
      const leader = bestIndex(values, measure);
      return html`
                  <tr>
                    <th scope="row">
                      ${measure.label}${measure.unit === void 0 ? null : html`<span class="unit">${measure.unit}</span>`}
                    </th>
                    ${values.map((value, index) => {
        const text = value === void 0 ? "—" : formatValue(value, measure, { locale: model.locale });
        return html`<td class="numeric ${index === leader ? "leading" : ""}">
                        ${text}
                      </td>`;
      })}
                  </tr>
                `;
    })}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }
};
function ranks(measure) {
  return normalizeBetterWhen(measure.betterWhen) !== "none";
}
function defaultSelection(model) {
  return model.ranked.filter((entry) => entry.rank !== null).slice(0, 3).map((entry) => entry.participant.id);
}
function bestIndex(values, measure) {
  const direction = normalizeBetterWhen(measure.betterWhen);
  if (direction === "none") return -1;
  let best = -1;
  let bestValue;
  values.forEach((value, index) => {
    if (typeof value !== "number") return;
    if (bestValue === void 0 || (direction === "lower" ? value < bestValue : value > bestValue)) {
      bestValue = value;
      best = index;
    }
  });
  return best;
}
function buildViewModel(document, options = {}) {
  const rankings = listRankings(document);
  const activeRanking = options.ranking !== void 0 && rankings.some((entry) => entry.id === options.ranking) ? options.ranking : rankings[0]?.id;
  return {
    document,
    ranked: rank(document, activeRanking),
    rankings,
    activeRanking,
    measures: orderMeasures(document),
    attributes: document.attributeDefinitions ?? [],
    events: buildEventTree(document.events ?? []),
    categories: document.categories ?? [],
    locale: options.locale ?? document.lang ?? "en"
  };
}
function orderMeasures(document) {
  const used = usedMeasures(document);
  const preferred = document.presentation?.measureOrder;
  if (preferred === void 0) return used;
  const position = new Map(preferred.map((id, index) => [id, index]));
  return [...used].sort(
    (a, b) => (position.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (position.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
}
function buildEventTree(events) {
  const nodes = new Map(
    events.map((event) => [event.id, { event, children: [] }])
  );
  const roots = [];
  for (const event of events) {
    const node = nodes.get(event.id);
    if (node === void 0) continue;
    const parent = event.parent === void 0 ? void 0 : nodes.get(event.parent);
    if (parent === void 0 || parent === node || createsCycle(events, event.id)) {
      roots.push(node);
    } else {
      parent.children.push(node);
    }
  }
  return roots;
}
function createsCycle(events, startId) {
  const parents = new Map(events.map((event) => [event.id, event.parent]));
  const seen = /* @__PURE__ */ new Set([startId]);
  let current = parents.get(startId);
  while (current !== void 0) {
    if (seen.has(current)) return true;
    seen.add(current);
    current = parents.get(current);
  }
  return false;
}
const viewerStyles = css`
  :host {
    --or-font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    --or-font-mono: ui-monospace, 'SF Mono', Menlo, monospace;
    --or-color-bg: #ffffff;
    --or-color-surface: #f7f6f4;
    --or-color-border: #e3e1dd;
    --or-color-text: #1c1b19;
    --or-color-muted: #6c6965;
    --or-color-accent: #2f5d8a;
    --or-color-highlight: #fff4d6;
    --or-radius: 6px;
    --or-space: 0.75rem;

    display: block;
    font-family: var(--or-font-family);
    color: var(--or-color-text);
    background: var(--or-color-bg);
    container-type: inline-size;
  }

  :host([theme='dark']) {
    --or-color-bg: #18191b;
    --or-color-surface: #1f2124;
    --or-color-border: #35383c;
    --or-color-text: #e9e7e4;
    --or-color-muted: #9b9894;
    --or-color-accent: #7fb0dd;
    --or-color-highlight: #3a3320;
  }

  @media (prefers-color-scheme: dark) {
    :host([theme='auto']) {
      --or-color-bg: #18191b;
      --or-color-surface: #1f2124;
      --or-color-border: #35383c;
      --or-color-text: #e9e7e4;
      --or-color-muted: #9b9894;
      --or-color-accent: #7fb0dd;
      --or-color-highlight: #3a3320;
    }
  }

  :host([compact]) {
    --or-space: 0.4rem;
    font-size: 0.9rem;
  }

  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--or-space);
    padding-bottom: var(--or-space);
    border-bottom: 1px solid var(--or-color-border);
  }

  .title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 650;
    letter-spacing: -0.01em;
  }

  .meta {
    color: var(--or-color-muted);
    font-size: 0.82rem;
  }

  .status-badge {
    padding: 0.05rem 0.4rem;
    border: 1px solid var(--or-color-border);
    border-radius: 999px;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--or-color-muted);
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: var(--or-space);
    margin-left: auto;
  }

  select {
    padding: 0.25rem 0.4rem;
    border: 1px solid var(--or-color-border);
    border-radius: var(--or-radius);
    background: var(--or-color-bg);
    color: var(--or-color-text);
    font: inherit;
    font-size: 0.82rem;
  }

  .body {
    padding-top: var(--or-space);
  }

  .scroll {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;
  }

  th,
  td {
    padding: 0.35rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid var(--or-color-border);
    white-space: nowrap;
  }

  thead th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--or-color-muted);
    font-weight: 600;
  }

  .numeric {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .unit {
    margin-left: 0.25rem;
    font-size: 0.85em;
    font-weight: 400;
    color: var(--or-color-muted);
  }

  .name {
    font-weight: 550;
    white-space: normal;
  }

  .status {
    color: var(--or-color-muted);
    font-size: 0.85em;
  }

  tr[data-unranked='true'] td,
  .standing[data-unranked='true'],
  .card[data-unranked='true'] {
    color: var(--or-color-muted);
  }

  .highlighted {
    background: var(--or-color-highlight);
  }

  .leading {
    font-weight: 650;
    color: var(--or-color-accent);
  }

  /* Ranking */
  .standings {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .standing {
    display: flex;
    align-items: center;
    gap: var(--or-space);
    padding: 0.45rem 0.5rem;
    border-bottom: 1px solid var(--or-color-border);
    cursor: pointer;
  }

  .standing:hover {
    background: var(--or-color-surface);
  }

  .position {
    min-width: 2.2rem;
    font-variant-numeric: tabular-nums;
    font-size: 1.05rem;
    font-weight: 650;
    text-align: right;
    color: var(--or-color-accent);
  }

  .who {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .attributes {
    font-size: 0.8rem;
    color: var(--or-color-muted);
  }

  .figures {
    display: flex;
    gap: var(--or-space);
    margin-left: auto;
    text-align: right;
  }

  .figure {
    display: flex;
    flex-direction: column;
  }

  .value {
    font-variant-numeric: tabular-nums;
    font-weight: 550;
  }

  .measure-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--or-color-muted);
  }

  .tied {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--or-color-muted);
  }

  /* Cards */
  .cards {
    display: grid;
    gap: var(--or-space);
    grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.75rem;
    border: 1px solid var(--or-color-border);
    border-radius: var(--or-radius);
    background: var(--or-color-surface);
    cursor: pointer;
  }

  .card-image {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    border-radius: calc(var(--or-radius) - 2px);
    background: var(--or-color-border);
  }

  .card-head {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }

  .card-head .name {
    margin: 0;
    font-size: 1rem;
  }

  .card-attributes,
  .card-measures {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.1rem 0.6rem;
    margin: 0;
    font-size: 0.82rem;
  }

  .card-attributes dt,
  .card-measures dt {
    color: var(--or-color-muted);
  }

  .card-attributes dd,
  .card-measures dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .notes {
    margin: 0.2rem 0 0;
    font-size: 0.8rem;
    color: var(--or-color-muted);
  }

  /* Compare */
  .compare-hint,
  .empty {
    margin: 0 0 var(--or-space);
    font-size: 0.82rem;
    color: var(--or-color-muted);
  }

  /* States */
  .message {
    padding: 1.25rem;
    border: 1px solid var(--or-color-border);
    border-radius: var(--or-radius);
    background: var(--or-color-surface);
    color: var(--or-color-muted);
    font-size: 0.9rem;
  }

  .message-error {
    border-color: #9c3232;
    color: #9c3232;
  }

  .footnote {
    margin-top: var(--or-space);
    font-size: 0.75rem;
    color: var(--or-color-muted);
  }

  @container (max-width: 34rem) {
    .figures .measure-label {
      display: none;
    }
  }
`;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _document, _error, _selection, _loadedSrc, _OpenResultViewer_instances, renderHead_fn, renderBody_fn, renderFootnote_fn, model_fn, changeView_fn, select_fn, announceLoad_fn, load_fn, fail_fn;
let OpenResultViewer = class extends LitElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _OpenResultViewer_instances);
    this.theme = "auto";
    this.compact = false;
    this._tick = 0;
    __privateAdd(this, _document);
    __privateAdd(this, _error);
    __privateAdd(this, _selection, []);
    __privateAdd(this, _loadedSrc);
  }
  set document(value) {
    __privateSet(this, _document, value);
    __privateSet(this, _error, void 0);
    __privateMethod(this, _OpenResultViewer_instances, announceLoad_fn).call(this);
  }
  get document() {
    return __privateGet(this, _document);
  }
  /** Views applicable to the current document, best first. */
  get availableViews() {
    const model = __privateMethod(this, _OpenResultViewer_instances, model_fn).call(this);
    if (model === void 0) return [];
    return applicableViews(model).map((candidate) => ({
      id: candidate.plugin.id,
      label: candidate.plugin.label,
      score: candidate.score
    }));
  }
  /** The ranking currently displayed. */
  get rankedEntries() {
    return __privateMethod(this, _OpenResultViewer_instances, model_fn).call(this)?.ranked ?? [];
  }
  willUpdate(changed) {
    if (changed.has("src") && this.src !== void 0 && this.src !== __privateGet(this, _loadedSrc)) {
      void __privateMethod(this, _OpenResultViewer_instances, load_fn).call(this, this.src);
    }
  }
  render() {
    if (__privateGet(this, _error) !== void 0) {
      return html`<div class="message message-error" role="alert">${__privateGet(this, _error).message}</div>`;
    }
    const model = __privateMethod(this, _OpenResultViewer_instances, model_fn).call(this);
    if (model === void 0) {
      return html`<div class="message">Nothing loaded yet.</div>`;
    }
    const plugin = selectView(model, this.view);
    return html`
      ${__privateMethod(this, _OpenResultViewer_instances, renderHead_fn).call(this, model, plugin)}
      <div class="body">${__privateMethod(this, _OpenResultViewer_instances, renderBody_fn).call(this, model, plugin)}</div>
    `;
  }
};
_document = /* @__PURE__ */ new WeakMap();
_error = /* @__PURE__ */ new WeakMap();
_selection = /* @__PURE__ */ new WeakMap();
_loadedSrc = /* @__PURE__ */ new WeakMap();
_OpenResultViewer_instances = /* @__PURE__ */ new WeakSet();
renderHead_fn = function(model, plugin) {
  const document = model.document;
  const candidates = applicableViews(model);
  return html`
      <header class="head">
        <h2 class="title">${document.title}</h2>
        ${document.status === void 0 ? nothing : html`<span class="status-badge">${document.status}</span>`}
        ${document.source?.name === void 0 ? nothing : html`<span class="meta">${document.source.name}</span>`}

        <div class="controls">
          ${model.rankings.length > 1 ? html`
                  <select
                    aria-label="Ranking"
                    .value=${model.activeRanking ?? ""}
                    @change=${(event) => {
    this.ranking = event.target.value;
  }}
                  >
                    ${model.rankings.map(
    (entry) => html`
                        <option value=${entry.id} ?selected=${entry.id === model.activeRanking}>
                          ${entry.label}
                        </option>
                      `
  )}
                  </select>
                ` : nothing}
          ${candidates.length > 1 ? html`
                  <select
                    aria-label="View"
                    @change=${(event) => __privateMethod(this, _OpenResultViewer_instances, changeView_fn).call(this, event.target.value)}
                  >
                    ${candidates.map(
    (candidate) => html`
                        <option
                          value=${candidate.plugin.id}
                          ?selected=${candidate.plugin.id === plugin?.id}
                        >
                          ${candidate.plugin.label}
                        </option>
                      `
  )}
                  </select>
                ` : nothing}
        </div>
      </header>
    `;
};
renderBody_fn = function(model, plugin) {
  if (model.ranked.length === 0) {
    return html`<div class="message">
        No results yet. The entry list and the ranking rule are published; the results are not.
      </div>`;
  }
  if (plugin === void 0) {
    return html`<div class="message">No registered view can render this document.</div>`;
  }
  try {
    return html`
        ${plugin.render({
      model,
      selection: __privateGet(this, _selection),
      onSelect: (participantId) => __privateMethod(this, _OpenResultViewer_instances, select_fn).call(this, participantId)
    })}
        ${__privateMethod(this, _OpenResultViewer_instances, renderFootnote_fn).call(this, model)}
      `;
  } catch (error) {
    console.error(error);
    return html`<div class="message message-error" role="alert">
        The “${plugin.label}” view could not render this document. Try another view.
      </div>`;
  }
};
renderFootnote_fn = function(model) {
  const active = model.rankings.find((entry) => entry.id === model.activeRanking);
  if (active === void 0) return nothing;
  return html`<p class="footnote">
      Ranked by ${active.sortBy.join(", ")} · ties:
      ${active.ties}${active.implicit ? " · derived, the document declares no ranking" : ""}
    </p>`;
};
model_fn = function() {
  if (__privateGet(this, _document) === void 0) return void 0;
  void this._tick;
  return buildViewModel(__privateGet(this, _document), {
    ...this.ranking === void 0 ? {} : { ranking: this.ranking },
    ...this.locale === void 0 ? {} : { locale: this.locale }
  });
};
changeView_fn = function(id) {
  const from = this.view;
  this.view = id;
  this.dispatchEvent(
    new CustomEvent("or-viewchange", { detail: { from, to: id }, bubbles: true, composed: true })
  );
};
select_fn = function(participantId) {
  __privateSet(this, _selection, __privateGet(this, _selection).includes(participantId) ? __privateGet(this, _selection).filter((id) => id !== participantId) : [...__privateGet(this, _selection), participantId]);
  this._tick += 1;
  this.dispatchEvent(
    new CustomEvent("or-select", {
      detail: { participantId, selection: [...__privateGet(this, _selection)] },
      bubbles: true,
      composed: true
    })
  );
};
announceLoad_fn = function() {
  this._tick += 1;
  if (__privateGet(this, _document) === void 0) return;
  this.dispatchEvent(
    new CustomEvent("or-load", {
      detail: { document: __privateGet(this, _document), views: this.availableViews },
      bubbles: true,
      composed: true
    })
  );
};
load_fn = async function(src) {
  __privateSet(this, _loadedSrc, src);
  __privateSet(this, _error, void 0);
  try {
    const response = await fetch(src);
    if (!response.ok) {
      __privateMethod(this, _OpenResultViewer_instances, fail_fn).call(this, "http-error", `Could not load ${src}: ${response.status} ${response.statusText}.`);
      return;
    }
    this.document = parse(await response.text());
  } catch (error) {
    if (error instanceof UnsupportedVersionError) {
      __privateMethod(this, _OpenResultViewer_instances, fail_fn).call(this, "unsupported-version", error.message);
    } else if (error instanceof NotOpenResultError) {
      __privateMethod(this, _OpenResultViewer_instances, fail_fn).call(this, "not-openresult", error.message);
    } else {
      __privateMethod(this, _OpenResultViewer_instances, fail_fn).call(this, "network", `Could not load ${src}. The server may not allow cross-origin requests.`);
      console.error(error);
    }
  }
};
fail_fn = function(code, message) {
  __privateSet(this, _error, { code, message });
  __privateSet(this, _document, void 0);
  this._tick += 1;
  this.dispatchEvent(
    new CustomEvent("or-error", { detail: { code, message }, bubbles: true, composed: true })
  );
};
OpenResultViewer.styles = viewerStyles;
__decorateClass([
  property({ type: String })
], OpenResultViewer.prototype, "src", 2);
__decorateClass([
  property({ type: String })
], OpenResultViewer.prototype, "view", 2);
__decorateClass([
  property({ type: String })
], OpenResultViewer.prototype, "ranking", 2);
__decorateClass([
  property({ type: String })
], OpenResultViewer.prototype, "locale", 2);
__decorateClass([
  property({ type: String, reflect: true })
], OpenResultViewer.prototype, "theme", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], OpenResultViewer.prototype, "compact", 2);
__decorateClass([
  property({ attribute: false })
], OpenResultViewer.prototype, "document", 1);
__decorateClass([
  state()
], OpenResultViewer.prototype, "_tick", 2);
OpenResultViewer = __decorateClass([
  customElement("openresult-viewer")
], OpenResultViewer);
registerView(rankingView);
registerView(cardsView);
registerView(compareView);
registerView(tableView);
export {
  OpenResultViewer,
  applicableViews,
  buildViewModel,
  cardsView,
  compareView,
  rankingView,
  registerView,
  registeredViews,
  selectView,
  tableView
};
//# sourceMappingURL=index.js.map
